import type { User } from '~/types/user'

export interface MessageAttachment {
  id: string
  uploader_id: string
  file_name: string
  content_type: string
  size_bytes: number
  status: string
  download_url: string
}

export interface TypingUser {
  userId: string
  username: string
  updatedAt: number
}

// Order matches the backend's `BotTypingState` enum (Program.cs) — the SignalR payload carries
// the numeric index, not the name, so position here is load-bearing.
export const BOT_TYPING_STATES = [
  'Thinking',
  'Explored',
  'Processing',
  'Reviewed',
  'Opened',
  'ReSearch',
  'Fetched',
] as const

export type BotTypingState = (typeof BOT_TYPING_STATES)[number]

export interface BotTypingUser {
  botId: string
  username: string
  state: BotTypingState
  updatedAt: number
  /** When this run of work started (first status after a pause) — kept across stage changes. */
  startedAt: number
  /**
   * The channel's newest message when the bot started working — usually the one that called it. The
   * agent's phantom message sits right after it in the list (MessageListInner), so whatever is
   * written while the bot works lands below the phantom, in time order. Id or optimistic local id.
   */
  anchorMessageId: string | null
}

/** A quick-reply button: clicking it sends a reply with `label`. Recommended renders primary, the rest secondary. */
export interface QuickReply {
  label: string
  recommended: boolean
}

/**
 * A message only the current user sees — Discord's «Ephemeral Messages». Never stored anywhere:
 * kept in memory only (stores/messages.ts `ephemeralByChannel`), gone after «Скрыть», after a quick
 * reply is used, or on restart. Comes from an AI agent (quick replies to whoever called it, the
 * `EphemeralMessageCreated` user event) or from an ephemeral slash-command/component response.
 */
export interface EphemeralMessage {
  id: string
  channel_id: string
  author_id: string
  content: string | null
  /** The message a quick reply answers (the agent's reply). */
  reply_to: string | null
  /** The message it renders right after — `reply_to`, or the newest message when it arrived. */
  anchor_id: string | null
  quick_replies: QuickReply[]
  components_json: string | null
  created_at: string
}

export interface ReactionUsers {
  user_ids: string[]
}

/**
 * A user, bot or agent named by a `<@id>` token in a message's content, as the server resolved it
 * (so a mention renders even when that member isn't loaded on this client, or has left).
 */
export interface MentionedUser {
  id: string
  username: string
  display_name: string
  avatar_url: string
  is_bot: boolean
}

/** A role named by a `<@&id>` token, resolved by the server (only when the mention took effect). */
export interface MentionedRole {
  id: string
  name: string
  /** RGB integer, 0 = no color. */
  color: number
}

/** A channel named by a `<#id>` token in a message's content, as the server resolved it. */
export interface MentionedChannel {
  id: string
  name: string
  /** `ChannelType` value. */
  type: number
  group_id: string
}

export interface Message {
  id: string
  channel_id: string
  author: User
  content: string
  created_at?: string
  updated_at?: string
  is_edited: boolean
  is_deleted: boolean
  reply_to: string
  forwarded_from: string
  interaction_id: string | null
  interaction_command_name: string | null
  interaction_user_id: string | null
  // JSON ActionRow[] (buttons/select/combobox/checkbox) attached by a bot — see
  // docs/message-components.md and `app/types/messageComponents.ts`.
  components_json: string | null
  attachments: MessageAttachment[]
  reactions: Record<string, ReactionUsers>
  // What the content's `<@id>` / `<#id>` tokens name (see lib/chat/mentionTokens.ts). Optional:
  // older messages, ephemeral ones and locally-built copies may not carry them.
  mentions?: MentionedUser[]
  mention_channels?: MentionedChannel[]
  // Role / @everyone / @here mentions that took effect, and the author of the replied-to message —
  // what the notification policy uses to decide whether this message mentions me.
  mention_roles?: MentionedRole[]
  mention_everyone?: boolean
  mention_here?: boolean
  reply_to_author_id?: string
  status?: 'sending' | 'sent' | 'failed'
  error?: string
  // The optimistic placeholder id this message was sent under, carried over once the server
  // (invoke result or realtime echo) replaces the placeholder with the real `id`. Lets the UI key
  // the row by one stable value across that swap (see `messageRenderKey`), so the sent bubble
  // doesn't remount mid-way through its enter animation. Never set on messages that were only
  // ever received.
  local_id?: string
}

export interface ForwardResult {
  channel_id: string
  success: boolean
  error_message: string
  message: Message | null
}
